import apiConfig from '../config/apiConfig';
import type { ScrapeProgressResponse } from './youtubeService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MyInstagramMediaItem {
  content_id: string;
  title: string;
  published_at: string | null;
  thumbnail_url?: string | null;
  url: string;
  media_type: string;
  scraped: boolean;
}

export interface MyInstagramMediaResponse {
  success: boolean;
  platform: string;
  ig_user: {
    ig_user_id: string;
    username: string;
  };
  max_lookback_days: number;
  window_start: string;
  window_end: string;
  posts: MyInstagramMediaItem[];
  summary: { listed: number; scraped: number; not_scraped: number };
}

export interface InstagramConnectionStatus {
  connected: boolean;
  ig_username?: string;
  ig_user_id?: string;
}

// ---------------------------------------------------------------------------
// OAuth helpers
// ---------------------------------------------------------------------------

export const getInstagramOAuthUrl = async (
  redirectUri: string,
  authToken: string,
): Promise<string> => {
  // Backend authenticates via get_current_user + CSRF-binds the OAuth
  // state to the caller's user_id. Omitting the Authorization header
  // silently 401'd the whole flow (same bug pattern as the YouTube OAuth
  // URL call). Mirror Gmail's pattern.
  const params = new URLSearchParams({ redirect_uri: redirectUri });
  const response = await fetch(
    `${apiConfig.baseUrl}/api/instagram/user/oauth-url?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to get Instagram OAuth URL');
  }
  const data = await response.json();
  return data.oauth_url;
};

export const exchangeInstagramCode = async (
  code: string,
  redirectUri: string,
  clerkToken: string,
  state: string | null,
): Promise<{
  success: boolean;
  ig_user_id: string;
  ig_username: string;
  ig_name: string;
  ig_profile_picture_url: string;
  ig_account_type: string;
}> => {
  const response = await fetch(
    `${apiConfig.baseUrl}/api/instagram/user/exchange-code`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${clerkToken}`,
      },
      body: JSON.stringify({ code, redirect_uri: redirectUri, state }),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Instagram code exchange failed');
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Connected account queries
// ---------------------------------------------------------------------------

export const getInstagramConnectionStatus = async (
  authToken: string,
): Promise<InstagramConnectionStatus> => {
  const response = await fetch(
    `${apiConfig.baseUrl}/api/instagram/user/connection-status`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      credentials: 'include',
    },
  );
  if (!response.ok) return { connected: false };
  return response.json();
};

export const getMyInstagramMedia = async (
  authToken: string,
  maxPosts: number = 50,
  lookbackDays?: number,
): Promise<MyInstagramMediaResponse> => {
  const params = new URLSearchParams();
  if (maxPosts) params.set('max_posts', String(maxPosts));
  if (lookbackDays) params.set('lookback_days', String(lookbackDays));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    `${apiConfig.baseUrl}/api/instagram/user/my-media${qs}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    const errType = response.headers.get('X-Error-Type');
    let msg = response.statusText;
    try {
      const body = await response.json();
      msg = body.detail || body.message || msg;
    } catch {
      /* ignore */
    }
    const e = new Error(msg) as Error & { status?: number; errorType?: string | null };
    e.status = response.status;
    e.errorType = errType;
    throw e;
  }
  return response.json();
};

export const disconnectInstagram = async (
  authToken: string,
): Promise<{ success: boolean }> => {
  const response = await fetch(
    `${apiConfig.baseUrl}/api/instagram/user/disconnect`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw new Error('Failed to disconnect Instagram');
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Async creator analysis (Sponsor Intel / Support — same job shape as YouTube)
// ---------------------------------------------------------------------------

export interface InstagramLookupResult {
  found: boolean;
  /** Where the data came from. "cache" = from a prior full IG analysis
   *  (richest, includes sponsorship history). "live_probe" = freshly
   *  fetched via Apify (avatar / followers / verified, no sponsorships).
   *  Absent on `found=false` results. */
  source?: 'cache' | 'live_probe';
  handle: string;
  creator_name?: string;
  full_name?: string;
  avatar_url?: string | null;
  followers_count?: number | null;
  is_verified?: boolean;
  bio?: string | null;
  sponsorships_count?: number;
  /** ISO 8601 timestamp from the cached analysis. Drives the
   *  "Last scraped X days ago" indicator in the analyze tile.
   *  Only set for `source: "cache"` results. */
  scraped_at?: string;
}

/**
 * Look up an Instagram creator's profile metadata.
 *
 * - `liveProbe=false` (default): cache-only path. Returns rich data only
 *   if we've previously analyzed the creator. Free.
 * - `liveProbe=true`: when the cache misses, the backend falls back to a
 *   lightweight Apify profile probe (~$0.0023/call, server-side cached
 *   for 24h). Returns real avatar / followers / verified for any public
 *   handle. Use this when you need rich data even for never-analyzed
 *   creators (e.g. populating the analyze-tile picker).
 *
 * Don't fan this out across many candidate handles per keystroke — the
 * backend caches each handle for 24h, but the first probe per handle
 * is real spend. Debounce + bound the candidate set.
 */
export const lookupInstagramCreator = async (
  handle: string,
  authToken?: string,
  liveProbe: boolean = false,
): Promise<InstagramLookupResult> => {
  const h = handle.replace(/^@/, '').trim();
  if (!h) return { found: false, handle: '' };
  const headers: Record<string, string> = {};
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  const url = `${apiConfig.baseUrl}/api/instagram/lookup?handle=${encodeURIComponent(h)}${liveProbe ? '&live_probe=true' : ''}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    return { found: false, handle: h };
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Multi-candidate Instagram search
// ---------------------------------------------------------------------------

/** One candidate row returned by `/api/instagram/search`. */
export interface InstagramCandidate {
  handle: string;
  full_name?: string | null;
  avatar_url?: string | null;
  followers_count?: number | null;
  is_verified?: boolean;
  is_private?: boolean;
  bio?: string | null;
  /** "cache" if we already have a prior analysis for this handle (richer
   *  signal — includes `sponsorships_count` + `scraped_at`). "live_probe"
   *  if the data came from a fresh Apify hit with no analysis history. */
  source?: 'cache' | 'live_probe';
  sponsorships_count?: number;
  scraped_at?: string;
}

export interface InstagramSearchResult {
  query: string;
  candidates: InstagramCandidate[];
}

/**
 * Search Instagram for multiple ranked candidate profiles matching a
 * free-form query.
 *
 * The backend generates plausible handle variations from the query (and
 * optionally any high-confidence handles passed in `extraHandles`, e.g.
 * the YouTube channel handle for the same person), probes them all via
 * a single Apify run, and returns the profiles that actually exist on
 * Instagram — ordered verified-first, then by descending follower count.
 *
 * Use this anywhere a user needs to pick the RIGHT Instagram account
 * from multiple possibilities before we commit a cross-platform link.
 * The previous single-handle inference path silently linked the wrong
 * account on every search where the canonical handle wasn't obvious from
 * the query (the Will Tennyson incident).
 *
 * Cost shape:
 *   - ~$0.005–$0.007 per uncached search (paid per real profile returned)
 *   - 24h server-side cache on (query, extraHandles) — repeat queries free
 *   - Should be DEBOUNCED if you wire it to a keystroke input
 */
export const searchInstagramProfiles = async (
  query: string,
  authToken: string,
  options?: {
    /** High-confidence handles to also probe (no @ prefix). Typically the
     *  YouTube channel handle for the same creator, since most creators
     *  use the same handle across platforms. */
    extraHandles?: string[];
    /** Max candidates to return. Backend caps at 10. */
    limit?: number;
  },
): Promise<InstagramSearchResult> => {
  const q = query.trim();
  if (!q) return { query: q, candidates: [] };
  const params = new URLSearchParams({ q });
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.extraHandles?.length) {
    const cleaned = options.extraHandles
      .map(h => h.replace(/^@/, '').toLowerCase().trim())
      .filter(Boolean);
    if (cleaned.length) params.set('extra_handles', cleaned.join(','));
  }
  const url = `${apiConfig.baseUrl}/api/instagram/search?${params.toString()}`;
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
      return { query: q, candidates: [] };
    }
    const body = await response.json();
    return {
      query: body.query || q,
      candidates: Array.isArray(body.candidates) ? body.candidates : [],
    };
  } catch {
    return { query: q, candidates: [] };
  }
};

export const startInstagramAnalysis = async (
  handle: string,
  maxPosts: number = 10,
  authToken?: string,
  forceRefresh = false,
): Promise<{ job_id: string }> => {
  const h = handle.replace(/^@/, '').trim();
  const max_posts = Math.min(Math.max(1, maxPosts), 20);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  const response = await fetch(`${apiConfig.baseUrl}/api/instagram/analyze-async`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ handle: h, max_posts, force_refresh: forceRefresh }),
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      const body = await response.json();
      msg = body.detail || body.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return response.json();
};

export const checkAnalysisProgress = async (
  jobId: string,
): Promise<ScrapeProgressResponse> => {
  const response = await fetch(
    `${apiConfig.baseUrl}/api/instagram/progress/${encodeURIComponent(jobId)}`,
  );
  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorData.message || errorMessage;
    } catch {
      /* ignore */
    }
    if (response.status === 404) {
      throw new Error('Analysis job not found. It may have expired or been cancelled.');
    }
    throw new Error(errorMessage);
  }
  return response.json();
};

const instagramService = {
  getInstagramOAuthUrl,
  exchangeInstagramCode,
  getInstagramConnectionStatus,
  getMyInstagramMedia,
  disconnectInstagram,
  startInstagramAnalysis,
  checkAnalysisProgress,
};

export default instagramService;
